<script setup lang="ts">
  import { useRouter } from 'vue-router';
  import { useTableFilterModal } from '@/shared/lib/use';
  import TrainingMatrixActionsBar from './TrainingMatrixActionsBar.vue';
  import TrainingMatrixTable from './TrainingMatrixTable.vue';
  import TrainingMatrixFilterModal from './TrainingMatrixFilterModal.vue';
  import TrainingMatrixRevisionSelectionModal from './TrainingMatrixRevisionSelectionModal.vue';
  import { useTrainingMatrixTable, type TrainingMatrixFilters } from '../model/useTrainingMatrixTable';
  import { useTrainingMatrixExport } from '../model/useTrainingMatrixExport';
  import { useTrainingMatrixSelection } from '../model/useTrainingMatrixSelection';
  import { useTrainingMatrixReport } from '../model/useTrainingMatrixReport';
  import { useEditTrainingMatrixRevision } from '../model/useEditTrainingMatrixRevision';
  import { useDeleteTrainingMatrixPositionEntries } from '../model/useDeleteTrainingMatrixPositionEntries';
  import { useBulkDeleteTrainingMatrixEntries } from '../model/useBulkDeleteTrainingMatrixEntries';
  import type { TrainingMatrixSearchField } from '@/entities/Training';

  const {
    positions,
    revision,
    metadata,
    loading,
    error,
    page,
    itemsPerPage,
    search,
    searchField,
    sort,
    order,
    positionId,
    documentPrefix,
    changePage,
    changeItemsPerPage,
    changeSearch,
    changeSearchField,
    changeSort,
    applyFilters,
    fetch,
    allPositions,
    hasMorePositions,
    loadingMorePositions,
  } = useTrainingMatrixTable();

  const { handleExportToExcel, isExporting, hasExportableItems } = useTrainingMatrixExport({
    metadata,
    search,
    searchField,
    sort,
    order,
    positionId,
    documentPrefix,
  });

  const selection = useTrainingMatrixSelection();

  const {
    isRevisionModalOpen,
    isLoadingRevisions,
    revisions,
    selectedRevisionId,
    hasAnyRevision,
    openRevisionModal,
    closeRevisionModal,
    selectRevision,
    confirmRevisionSelection,
    refreshRevisionAvailability,
  } = useTrainingMatrixReport();

  async function handleRevisionSaved(): Promise<void> {
    await fetch();
    await refreshRevisionAvailability();
  }

  const revisionEdit = useEditTrainingMatrixRevision(handleRevisionSaved);

  const { isFilterActive, openFilterModal } = useTableFilterModal<TrainingMatrixFilters>(
    TrainingMatrixFilterModal,
    { clearFilters: () => {}, filters: {} },
    applyFilters,
    () => ({ positionId: positionId.value, documentPrefix: documentPrefix.value })
  );

  const router = useRouter();

  function goToEditPosition(editedPositionId: number): void {
    router.push({ name: 'TreinamentosMatrizEditarCargo', params: { positionId: editedPositionId } });
  }

  function goToEditBatch(editedPositionIds: number[]): void {
    router.push({
      name: 'TreinamentosMatrizEditarEmLote',
      // Omitting positionIds means "edit the whole matrix" — see useTrainingMatrixEditBatch.
      query: editedPositionIds.length > 0 ? { positionIds: editedPositionIds.join(',') } : {},
    });
  }

  function handleEditSelectedClicked(): void {
    const selectedIds = Array.from(selection.selectedIds.value);
    if (selectedIds.length === 1) {
      goToEditPosition(selectedIds[0]);
    } else {
      goToEditBatch(selectedIds);
    }
  }

  async function handleMatrixEntriesDeleted(): Promise<void> {
    changeSearch('');
    applyFilters({});
    await fetch();
  }

  // The refetch goes back to page 1, so the deleted position may not be on screen to resync.
  async function handlePositionEntriesDeleted(positionId: number): Promise<void> {
    selection.markWithoutDocuments(positionId);
    await handleMatrixEntriesDeleted();
  }

  const deletePosition = useDeleteTrainingMatrixPositionEntries(handlePositionEntriesDeleted);

  async function handleBulkMatrixEntriesDeleted(): Promise<void> {
    selection.clear();
    await handleMatrixEntriesDeleted();
  }

  const bulkDeletePositions = useBulkDeleteTrainingMatrixEntries(
    () => selection.selectedIdsWithDocuments.value,
    handleBulkMatrixEntriesDeleted
  );
</script>

<template>
  <div
    data-testid="training-matrix-tab-page"
    class="flex flex-col gap-4"
  >
    <TrainingMatrixActionsBar
      :can-generate-matrix="hasAnyRevision"
      :search-field-id="searchField"
      :search-term="search"
      :export-disabled="!hasExportableItems"
      :is-exporting="isExporting"
      @generate-matrix="openRevisionModal"
      @export-excel="handleExportToExcel"
      @update:search-field-id="(value) => changeSearchField(value as TrainingMatrixSearchField)"
      @search="changeSearch"
    />

    <TrainingMatrixTable
      :positions="positions"
      :metadata="metadata"
      :revision="revision"
      :loading="loading"
      :error="error"
      :page="page"
      :items-per-page="itemsPerPage"
      :sort="sort"
      :order="order"
      :is-filter-active="isFilterActive"
      :selection="selection"
      :revision-edit="revisionEdit"
      :all-positions="allPositions"
      :has-more-positions="hasMorePositions"
      :loading-more-positions="loadingMorePositions"
      @change-page="changePage"
      @change-items-per-page="changeItemsPerPage"
      @update:sort="changeSort"
      @filter-clicked="openFilterModal"
      @edit-position="goToEditPosition"
      @edit-selected-clicked="handleEditSelectedClicked"
      @delete-position="deletePosition.requestDelete"
      @delete-selected-clicked="bulkDeletePositions.requestDelete"
    />

    <TrainingMatrixRevisionSelectionModal
      v-if="isRevisionModalOpen"
      :revisions="revisions"
      :loading="isLoadingRevisions"
      :selected-revision-id="selectedRevisionId"
      @close="closeRevisionModal"
      @select="selectRevision"
      @confirm="confirmRevisionSelection"
    />
  </div>
</template>
